import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  EditOutlined,
} from '@ant-design/icons'
import { Button, Descriptions, Divider, Modal, Tag } from 'antd'
import { useEffect, useState } from 'react'

import Loader from '@/components/Atoms/Loader/Loader'
import {
  PartnershipDetailsModel,
  PartnershipSettingsDetailsDto,
  StoreShortModel,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { sortStores } from '@/utils/search/stores/sortStores'

import cn from './PartnershipDetailsModal.module.scss'

interface Props {
  open: boolean
  partnershipId: number | null
  initialIsIncoming: boolean | null
  onClose: () => void
  onEdit: () => void
}

function StoreTags({
  store,
  color,
}: {
  store: StoreShortModel
  color: string
}) {
  return (
    <span className={cn.storeTags}>
      <Tag color={color}>{store.description}</Tag>
      <span
        className={`${cn.storeStatusDot} ${store.isActive ? cn.activeStoreStatus : cn.inactiveStoreStatus}`}
        title={store.isActive ? 'Активен' : 'Неактивен'}
        aria-label={store.isActive ? 'Активен' : 'Неактивен'}
      />
      {store.isRemote && <Tag color="default">Удалённый</Tag>}
    </span>
  )
}

export default function PartnershipDetailsModal({
  open,
  partnershipId,
  initialIsIncoming,
  onClose,
  onEdit,
}: Props) {
  const [data, setData] = useState<PartnershipDetailsModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [modal, contextHolder] = Modal.useModal()

  useEffect(() => {
    if (open && partnershipId) {
      setLoading(true)
      setData(null)

      api
        .get<{ partnership: PartnershipDetailsModel }>(
          `/partnerships/${partnershipId}`
        )
        .then((res) => {
          const result = res.data.partnership
          setData(result)
        })
        .catch((e) => {
          handleApiError(e, modal)
          onClose()
        })
        .finally(() => setLoading(false))
    }
  }, [open, partnershipId])

  const renderSettingsSection = (
    settings: PartnershipSettingsDetailsDto,
    title: string
  ) => {
    return (
      <div className={cn.settingsBlock}>
        <div className={cn.blockHeader}>
          <h4>{title}</h4>
          <Tag color={settings.sharesData ? 'green' : 'default'}>
            {settings.sharesData
              ? 'Трансляция включена'
              : 'Трансляция выключена'}
          </Tag>
        </div>

        <Descriptions
          column={1}
          size="small"
          bordered
          className={cn.descriptions}
        >
          <Descriptions.Item label="Срок доставки (ETA)">
            {settings.etaDays > 0 ? `${settings.etaDays} дн.` : '0 дн.'}
          </Descriptions.Item>

          <Descriptions.Item label="Дополнительное время на приемку">
            {settings.incomingEtaDays > 0
              ? `${settings.incomingEtaDays} дн.`
              : '0 дн.'}
          </Descriptions.Item>

          <Descriptions.Item label="Исходящие исключения (скрытые склады)">
            <div className={cn.tagList}>
              {settings.outgoingExcludedStores?.length > 0 ? (
                sortStores(settings.outgoingExcludedStores).map((s) => (
                  <StoreTags key={s.code} store={s} color="warning" />
                ))
              ) : (
                <span className={cn.emptyValue}>Все склады транслируются</span>
              )}
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Входящие исключения (блокируемые склады)">
            <div className={cn.tagList}>
              {settings.incomingExcludedStores?.length > 0 ? (
                sortStores(settings.incomingExcludedStores).map((s) => (
                  <StoreTags key={s.code} store={s} color="orange" />
                ))
              ) : (
                <span className={cn.emptyValue}>
                  Все входящие склады принимаются
                </span>
              )}
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Доступные сегменты">
            <div className={cn.tagList}>
              {settings.sharedSegments?.length > 0 ? (
                settings.sharedSegments.map((s) => (
                  <Tag key={s.id} color="blue">
                    {s.description}
                  </Tag>
                ))
              ) : (
                <span className={cn.emptyValue}>Сегменты не выбраны</span>
              )}
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="B2B сегмент (цены)">
            {settings.b2bSegment ? (
              <Tag color="purple">{settings.b2bSegment.description}</Tag>
            ) : (
              <span className={cn.emptyValue}>Не выбран</span>
            )}
          </Descriptions.Item>
        </Descriptions>
      </div>
    )
  }

  const isIncomingProposal = data
    ? data.status === 'PROPOSED' && !data.initiatedByMe
    : !!initialIsIncoming
  const canAction = data
    ? data.status === 'ACTIVE' ||
      (data.status === 'PROPOSED' && !data.initiatedByMe)
    : true

  return (
    <Modal
      title={
        <div className={cn.modalTitle}>
          <span>Детали партнёрства</span>
          <strong>
            {data?.partner?.description || (loading ? 'Загрузка...' : '')}
          </strong>
        </div>
      }
      open={open}
      onCancel={onClose}
      className={cn.detailsModal}
      width={750}
      centered
      footer={
        <div className={cn.footerButtons}>
          <Button onClick={onClose} size="large">
            Закрыть
          </Button>
          <Button
            type="primary"
            icon={isIncomingProposal ? <CheckOutlined /> : <EditOutlined />}
            onClick={onEdit}
            size="large"
            disabled={loading || !data || !canAction}
            loading={loading}
          >
            {isIncomingProposal
              ? 'Принять партнёрство'
              : 'Изменить мои настройки'}
          </Button>
        </div>
      }
    >
      {contextHolder}

      {loading ? (
        <div className={cn.loaderContainer}>
          <Loader size={40} />
        </div>
      ) : data ? (
        <div className={cn.content}>
          {renderSettingsSection(data.mySettings, 'Мои настройки')}

          <div className={cn.dividerArea}>
            <Divider plain>
              <ArrowDownOutlined
                className={`${cn.exchangeArrow} ${cn.exchangeArrowLeft}`}
              />
              взаимный обмен данными
              <ArrowUpOutlined
                className={`${cn.exchangeArrow} ${cn.exchangeArrowRight}`}
              />
            </Divider>
          </div>

          {renderSettingsSection(data.partnerSettings, 'Настройки партнёра')}
        </div>
      ) : null}
    </Modal>
  )
}
